import {
  ApiUnauthorizedError,
  configureAdminBff,
  formatApiError,
  setAdminBffAccessToken,
} from '@avila-roffe/api-clients-admin-bff';
import { configureArafFactory, updateArafBearerToken } from '@avila-roffe/api-clients-araf-factory';
import { Toaster, toast } from '@avila-roffe/components-web';
import { applyLocale, I18nProvider, LOCALE_STORAGE_KEY } from '@avila-roffe/i18n';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import './styles.css';
import App from './app/app';
import { AuthProvider } from './auth/AuthProvider';
import { initFaro } from './lib/faro';
import { fetchBootstrapConfig } from './lib/fetchBootstrapConfig';
import { initNativePlugins } from './lib/platform';
import { removeSplash } from './lib/splash';
import { FactoryWsProvider } from './providers/FactoryWsProvider';
import { useAuthStore } from './stores/authStore';
import { usePreferencesStore } from './stores/preferencesStore';
import { TenantBootstrapProvider } from './tenant/TenantBootstrapProvider';

void initNativePlugins();

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js');
  });
}

function redirectToLogin() {
  window.location.replace('/login');
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof ApiUnauthorizedError) {
        redirectToLogin();
        return;
      }
      const { title, description } = formatApiError(error);
      toast.error(title, { description });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiUnauthorizedError) return false;
        return failureCount < 1;
      },
    },
  },
});

async function bootstrap() {
  const config = await fetchBootstrapConfig();

  // Route all adminBffFetch calls through CF BFF (VITE_ARAF_FACTORY_URL).
  // CF BFF handles CF routes directly and proxies the rest to admin-bff internally.
  // Direct fetch() calls in auth flows (token refresh, bootstrap) still use
  // VITE_ADMIN_BFF_URL so httpOnly cookies are scoped to the correct domain.
  const cfBffUrl = (import.meta.env.VITE_ARAF_FACTORY_URL as string | undefined) ?? '';
  configureAdminBff({
    baseUrl:
      cfBffUrl ||
      config?.bffUrl ||
      (import.meta.env.VITE_ADMIN_BFF_URL as string | undefined) ||
      'http://localhost:3601',
  });

  configureArafFactory({
    baseUrl: (import.meta.env.VITE_ARAF_FACTORY_URL as string | undefined) ?? '',
    adminToken: (import.meta.env.VITE_ARAF_ADMIN_TOKEN as string | undefined) || '',
  });

  // Pre-seed the API client token from localStorage so React Query doesn't
  // fire with no Authorization header before AuthProvider.init() completes.
  const { accessToken: storedToken, tokenExpiry: storedExpiry } = useAuthStore.getState();
  if (storedToken && storedExpiry !== null && storedExpiry > Date.now() + 30_000) {
    setAdminBffAccessToken(storedToken);
    updateArafBearerToken(storedToken);
  }

  initFaro();

  // Apply stored locale before React mounts to avoid language flash.
  const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
  const { language } = usePreferencesStore.getState();
  const initialLocale = language !== 'en' ? language : (storedLocale ?? 'en');
  if (initialLocale !== 'en') {
    applyLocale(initialLocale);
  }

  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

  // Safety net: if the React tree fails to mount (e.g. uncaught error before ThemedApp),
  // the HTML splash would stay forever. Remove it after 4 seconds at most.
  setTimeout(removeSplash, 4000);

  root.render(
    <I18nProvider>
      <StrictMode>
        <TenantBootstrapProvider>
          <AuthProvider>
            <QueryClientProvider client={queryClient}>
              <FactoryWsProvider>
                <App />
                <Toaster position="bottom-right" richColors />
              </FactoryWsProvider>
            </QueryClientProvider>
          </AuthProvider>
        </TenantBootstrapProvider>
      </StrictMode>
    </I18nProvider>,
  );
}

bootstrap().catch(console.error);
